import React from 'react';
import { Cloud, Zap, Code, Brain, Shield, Settings, Server, Layers } from 'lucide-react';
import SEO from '../components/SEO';

export default function Solutions() {
  const canonical = 'https://divaisetech.com/solutions';
  const servicePageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'IT Solutions and Services',
    description: 'Comprehensive cloud, DevOps, development, AI/ML, hardware, and security services for businesses.',
    url: canonical,
    provider: {
      '@type': 'Organization',
      name: 'DivaiseTech',
      url: 'https://divaisetech.com',
      logo: 'https://divaisetech.com/logo.png',
    },
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
    serviceType: 'Technology Consulting and Managed Services',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
    },
  };

  const solutionsSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'IT Solutions and Services',
    serviceType: 'Cloud, DevOps, AI/ML, Hardware and Security Services',
    provider: {
      '@type': 'Organization',
      name: 'Divaise Technology Private Limited',
      url: 'https://divaisetech.com',
    },
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
  };

  const services = [
    {
      icon: Cloud,
      title: 'Cloud Solutions',
      description: 'Design, migration and management of cloud infrastructure.',
      details: [
        'Multi-cloud strategy & architecture',
        'AWS, Azure, GCP expertise',
        'Serverless & containerized solutions',
        'Cloud cost optimization',
        'Disaster recovery & business continuity',
      ],
    },
    {
      icon: Zap,
      title: 'DevOps & Infrastructure',
      description: 'CI/CD pipelines, Kubernetes, and automated deployments.',
      details: [
        'Jenkins, GitLab CI, GitHub Actions setup',
        'Kubernetes orchestration',
        'Infrastructure as Code (Terraform, CloudFormation)',
        'Monitoring & observability',
        'Automated scaling & load balancing',
      ],
    },
    {
      icon: Code,
      title: 'Custom Development',
      description: 'Full-stack web and mobile applications.',
      details: [
        'Frontend (React, Vue, Angular)',
        'Backend (Node.js, Python, Java)',
        'Mobile (iOS, Android, React Native)',
        'API design & integration',
        'Database optimization',
      ],
    },
    {
      icon: Brain,
      title: 'AI / ML Services',
      description: 'Machine learning models and data-driven solutions.',
      details: [
        'Predictive analytics & forecasting',
        'Computer vision solutions',
        'NLP & chatbot development',
        'Data pipeline & ETL',
        'Model training & optimization',
      ],
    },
    {
      icon: Server,
      title: 'Hardware Solutions',
      description: 'Hardware planning, sourcing, and deployment for AI and enterprise workloads.',
      details: [
        'AI chip and accelerator requirement planning',
        'GPU and compute server recommendations',
        'Edge hardware and appliance deployment',
        'Vendor coordination and fulfillment support',
        'Installation, validation, and handover documentation',
      ],
    },
    {
      icon: Layers,
      title: 'Digital Experience',
      description: 'Adobe Cloud-based digital experience implementation and optimization services.',
      details: [
        'Adobe Experience Manager (AEM) Sites architecture and implementation',
        'Adobe Commerce storefront strategy, integration, and optimization',
        'AEM Assets (DAM) setup, taxonomy, workflows, and governance',
        'Adobe Launch tag management, analytics integration, and release controls',
        'Cross-channel personalization and content operations enablement',
      ],
    },
    {
      icon: Shield,
      title: 'Security & Compliance',
      description: 'Vulnerability assessments and regulatory compliance.',
      details: [
        'Penetration testing',
        'Security audits & assessments',
        'Security policy and control framework setup',
        'GDPR, HIPAA compliance',
        'Secure software development lifecycle',
      ],
    },
    {
      icon: Settings,
      title: 'Managed Services',
      description: '24/7 monitoring, support and optimization.',
      details: [
        '24/7 infrastructure monitoring',
        'Incident response & management',
        'Performance optimization',
        'Regular patching & updates',
        'Dedicated account management',
      ],
    },
  ];

  return (
    <div>
      <SEO
        title="IT Solutions & Services - Divaise Technology"
        description="Comprehensive IT solutions including cloud computing, infrastructure management, custom software development, AI/ML services, and hardware solutions."
        keywords="IT solutions, cloud services, DevOps, custom development, AI/ML, security, hardware solutions, AI chips"
        canonical={canonical}
        schema={[solutionsSchema, servicePageSchema]}
      />

      {/* Hero Section */}
      <section className="hero-with-image hero-solutions text-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl font-bold mb-6">Our Solutions</h1>
          <p className="text-xl opacity-90 max-w-3xl">
            Comprehensive IT services designed to address every aspect of your technology infrastructure
            and business requirements.
          </p>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="bg-black py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={service.title}
                  className="bg-white border border-border rounded-xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 hover:border-accent"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-7 h-7 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-black">{service.title}</h3>
                      <p className="text-gray-700">{service.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {service.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="w-2 h-2 rounded-full bg-accent mt-2 flex-shrink-0"></span>
                        <span className="text-gray-800">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 py-20 px-6 border-y border-gray-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-white mb-4">Adobe Cloud Digital Experience</h2>
          <p className="text-white/80 mb-10 max-w-3xl">
            End-to-end Adobe ecosystem services for enterprises that need scalable content,
            commerce, and measurable customer journeys.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-border rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-2">AEM Sites</h3>
              <p className="text-black/80 text-sm">Template and component engineering, authoring workflows, and multi-site governance.</p>
            </div>
            <div className="bg-white border border-border rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-2">Adobe Commerce</h3>
              <p className="text-black/80 text-sm">Commerce architecture, checkout optimization, integrations, and conversion-focused delivery.</p>
            </div>
            <div className="bg-white border border-border rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-2">AEM Assets</h3>
              <p className="text-black/80 text-sm">DAM setup with metadata standards, approval workflows, and content reuse at scale.</p>
            </div>
            <div className="bg-white border border-border rounded-xl p-6">
              <h3 className="text-lg font-bold text-black mb-2">Adobe Launch</h3>
              <p className="text-black/80 text-sm">Tag strategy, event instrumentation, and controlled publishing across properties.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="bg-black py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">Technology Stack We Work With</h2>
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <h4 className="font-bold mb-4 text-white">Cloud Providers</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>Amazon Web Services</li>
                <li>Microsoft Azure</li>
                <li>Google Cloud Platform</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-white">DevOps Tools</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>Docker & Kubernetes</li>
                <li>Terraform</li>
                <li>Jenkins, GitLab CI</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-white">Development</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>React, Node.js</li>
                <li>Python, Java</li>
                <li>Go, Rust</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-white">Databases</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>PostgreSQL, MySQL</li>
                <li>Redis, Elasticsearch</li>
                <li>DynamoDB</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-white">Hardware & Compute</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>AI accelerators and chip planning</li>
                <li>GPU workstations and inference servers</li>
                <li>Edge gateways and on-prem compute</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="bg-gray-900 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">Our Engagement Models</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
              <h3 className="text-2xl font-bold mb-4 text-white">Project Based</h3>
              <p className="text-white/70 mb-4">
                Fixed scope, timeline, and budget. Perfect for well-defined projects with clear requirements.
              </p>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full"></span>
                  Clear deliverables
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full"></span>
                  Fixed timeline
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full"></span>
                  Regular milestones
                </li>
              </ul>
            </div>
            <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
              <h3 className="text-2xl font-bold mb-4 text-white">Time & Materials</h3>
              <p className="text-white/70 mb-4">
                Flexible approach for evolving requirements. Pay for actual hours and resources used.
              </p>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full"></span>
                  Flexible scope
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full"></span>
                  Adaptive timeline
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full"></span>
                  Transparent billing
                </li>
              </ul>
            </div>
            <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
              <h3 className="text-2xl font-bold mb-4 text-white">Dedicated Team</h3>
              <p className="text-white/70 mb-4">
                Extended team resources working exclusively on your projects. Optimal utilization and continuity.
              </p>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full"></span>
                  Exclusive team
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full"></span>
                  Long-term support
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full"></span>
                  Team scaling
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
